import { useState, useEffect } from "react";
import { format, addDays, startOfToday, isBefore, isWeekend } from "date-fns";
import { it } from "date-fns/locale";
import { Calendar as CalendarIcon, Clock, User, Mail, Phone, FileText, CheckCircle } from "lucide-react";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const services = [
  { value: "isee", label: "ISEE e DSU" },
  { value: "730", label: "Modello 730" },
  { value: "pensioni", label: "Pensioni" },
  { value: "inps", label: "Pratiche INPS" },
  { value: "immigrazione", label: "Immigrazione" },
  { value: "successioni", label: "Successioni" },
  { value: "udicon", label: "U.Di.Con - Tutela Consumatori" },
  { value: "altro", label: "Altro" },
];

const Prenota = () => {
  const [step, setStep] = useState(1);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    service: "",
    notes: "",
  });

  const today = startOfToday();
  const maxDate = addDays(today, 60);

  // Fetch availability when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailability(format(selectedDate, "yyyy-MM-dd"));
    }
  }, [selectedDate]);

  const fetchAvailability = async (dateStr) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/availability/${dateStr}`);
      if (response.data.available) {
        setAvailableSlots(response.data.slots.filter(s => s.available));
      } else {
        setAvailableSlots([]);
        toast.info(response.data.message || "Giornata non disponibile");
      }
    } catch (error) {
      console.error("Error fetching availability:", error);
      setAvailableSlots([]);
      toast.error("Errore nel caricamento delle disponibilità");
    } finally {
      setLoading(false);
    }
  };

  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null);
  };

  const handleTimeSelect = (time) => {
    setSelectedTime(time);
    setStep(2);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedDate || !selectedTime || !formData.name || !formData.email || !formData.phone || !formData.service) {
      toast.error("Compila tutti i campi obbligatori");
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, {
        ...formData,
        date: format(selectedDate, "yyyy-MM-dd"),
        time: selectedTime,
      });
      setSuccess(true);
      toast.success("Appuntamento prenotato con successo!");
    } catch (error) {
      console.error("Error booking appointment:", error);
      toast.error(error.response?.data?.detail || "Errore nella prenotazione");
    } finally {
      setSubmitting(false);
    }
  };

  const disabledDays = (date) => {
    return isBefore(date, today) || isBefore(maxDate, date);
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-50 py-20">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="bg-white rounded-2xl p-12 shadow-lg">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <h1 className="font-heading text-3xl font-bold text-slate-900 mb-4">
              Prenotazione Confermata!
            </h1>
            <p className="text-lg text-slate-600 mb-8">
              Grazie <strong>{formData.name}</strong>! Il tuo appuntamento è stato prenotato per:
            </p>
            <div className="bg-slate-50 rounded-xl p-6 mb-8">
              <div className="flex items-center justify-center gap-8">
                <div className="flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-blue-900" />
                  <span className="font-semibold">
                    {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-900" />
                  <span className="font-semibold">{selectedTime}</span>
                </div>
              </div>
              <p className="mt-4 text-slate-600">
                Servizio: <strong>{services.find(s => s.value === formData.service)?.label}</strong>
              </p>
            </div>
            <p className="text-slate-500 text-sm mb-8">
              Riceverai una conferma via email all'indirizzo {formData.email}
            </p>
            <Button
              onClick={() => {
                setSuccess(false);
                setStep(1);
                setSelectedDate(null);
                setSelectedTime(null);
                setFormData({ name: "", email: "", phone: "", service: "", notes: "" });
              }}
              variant="outline"
              className="rounded-full"
              data-testid="book-another"
            >
              Prenota un altro appuntamento
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Hero */}
      <section className="bg-red-600 py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tighter text-white mb-4">
              Prenota il tuo appuntamento
            </h1>
            <p className="text-xl text-red-100">
              Scegli data, orario e servizio. Ti aspettiamo presso il nostro ufficio.
            </p>
          </div>
        </div>
      </section>

      {/* Progress Steps */}
      <section className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-center gap-4">
            <div className={`flex items-center gap-2 ${step >= 1 ? "text-blue-900" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                step >= 1 ? "bg-blue-900 text-white" : "bg-slate-200"
              }`}>
                1
              </div>
              <span className="font-medium hidden sm:inline">Data e Ora</span>
            </div>
            <div className={`w-16 h-0.5 ${step >= 2 ? "bg-blue-900" : "bg-slate-200"}`} />
            <div className={`flex items-center gap-2 ${step >= 2 ? "text-blue-900" : "text-slate-400"}`}>
              <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                step >= 2 ? "bg-blue-900 text-white" : "bg-slate-200"
              }`}>
                2
              </div>
              <span className="font-medium hidden sm:inline">I tuoi dati</span>
            </div>
          </div>
        </div>
      </section>

      {/* Booking Content */}
      <section className="py-12 md:py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {step === 1 ? (
            <div className="grid lg:grid-cols-2 gap-8">
              {/* Calendar */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <CalendarIcon className="w-5 h-5 text-blue-900" />
                  Seleziona una data
                </h2>
                <Calendar
                  mode="single"
                  selected={selectedDate}
                  onSelect={handleDateSelect}
                  disabled={disabledDays}
                  locale={it}
                  className="rounded-md border mx-auto"
                  data-testid="booking-calendar"
                />
              </div>

              {/* Time Slots */}
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                <h2 className="font-heading text-xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-900" />
                  Seleziona un orario
                </h2>
                
                {!selectedDate ? (
                  <div className="text-center py-12 text-slate-500">
                    <CalendarIcon className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Seleziona prima una data dal calendario</p>
                  </div>
                ) : loading ? (
                  <div className="text-center py-12">
                    <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-900 mx-auto"></div>
                    <p className="mt-4 text-slate-500">Caricamento disponibilità...</p>
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="text-center py-12 text-slate-500">
                    <Clock className="w-12 h-12 mx-auto mb-4 opacity-50" />
                    <p>Nessun orario disponibile per questa data</p>
                    <p className="text-sm mt-2">Prova a selezionare un altro giorno</p>
                  </div>
                ) : (
                  <>
                    <p className="text-slate-600 mb-4">
                      {format(selectedDate, "EEEE d MMMM yyyy", { locale: it })}
                    </p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot.time}
                          variant={selectedTime === slot.time ? "default" : "outline"}
                          className={`rounded-lg h-12 ${
                            selectedTime === slot.time 
                              ? "bg-blue-900 text-white" 
                              : "hover:border-blue-900 hover:text-blue-900"
                          }`}
                          onClick={() => handleTimeSelect(slot.time)}
                          data-testid={`time-slot-${slot.time.replace(":", "")}`}
                        >
                          {slot.time}
                        </Button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          ) : (
            <div className="max-w-2xl mx-auto">
              <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm">
                {/* Selected Date/Time Summary */}
                <div className="bg-slate-50 rounded-xl p-4 mb-8 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="flex items-center gap-2 text-slate-700">
                      <CalendarIcon className="w-5 h-5 text-blue-900" />
                      <span className="font-medium">
                        {format(selectedDate, "EEE d MMM", { locale: it })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-700">
                      <Clock className="w-5 h-5 text-blue-900" />
                      <span className="font-medium">{selectedTime}</span>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => setStep(1)}
                    data-testid="change-datetime"
                  >
                    Modifica
                  </Button>
                </div>

                <h2 className="font-heading text-xl font-bold text-slate-900 mb-6">
                  Inserisci i tuoi dati
                </h2>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="space-y-2">
                    <Label htmlFor="name" className="flex items-center gap-2">
                      <User className="w-4 h-4" />
                      Nome e Cognome *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Mario Rossi"
                      required
                      className="h-12"
                      data-testid="input-name"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="email" className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="mario.rossi@email.com"
                      required
                      className="h-12"
                      data-testid="input-email"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone" className="flex items-center gap-2">
                      <Phone className="w-4 h-4" />
                      Telefono *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="333 1234567"
                      required
                      className="h-12"
                      data-testid="input-phone"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="service" className="flex items-center gap-2">
                      <FileText className="w-4 h-4" />
                      Servizio richiesto *
                    </Label>
                    <Select 
                      value={formData.service} 
                      onValueChange={handleServiceChange}
                      required
                    >
                      <SelectTrigger 
                        className="h-12"
                        data-testid="select-service"
                      >
                        <SelectValue placeholder="Seleziona un servizio" />
                      </SelectTrigger>
                      <SelectContent>
                        {services.map((service) => (
                          <SelectItem 
                            key={service.value} 
                            value={service.value}
                            data-testid={`service-option-${service.value}`}
                          >
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Note aggiuntive (opzionale)</Label>
                    <Textarea
                      id="notes"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      placeholder="Informazioni aggiuntive sulla tua richiesta..."
                      rows={4}
                      data-testid="input-notes"
                    />
                  </div>

                  <div className="flex gap-4 pt-4">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setStep(1)}
                      className="flex-1 h-12 rounded-full"
                      data-testid="back-button"
                    >
                      Indietro
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      className="flex-1 h-12 bg-red-600 hover:bg-red-700 text-white rounded-full"
                      data-testid="submit-booking"
                    >
                      {submitting ? "Prenotazione in corso..." : "Conferma Prenotazione"}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CalendarIcon className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Orari di apertura</h3>
              <p className="text-slate-600">Lun - Ven: 09:00 - 18:00</p>
              <p className="text-slate-500 text-sm">Pausa: 13:00 - 14:00</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Phone className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Contatto telefonico</h3>
              <a href="tel:0883411359" className="text-blue-900 font-semibold">0883 411359</a>
              <p className="text-slate-500 text-sm">Per urgenze o informazioni</p>
            </div>
            <div className="text-center">
              <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-6 h-6 text-blue-900" />
              </div>
              <h3 className="font-heading font-bold text-slate-900 mb-2">Disdetta</h3>
              <p className="text-slate-600">Avvisa almeno 24h prima</p>
              <p className="text-slate-500 text-sm">Via telefono o email</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Prenota;
